<script setup>
import { computed, inject } from 'vue';
import { ctaButtons, smartphoneScreens } from '../blockContent.js';
import SmartphonePreview from '../SmartphonePreview.vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const align = computed(() => (['left', 'center', 'right'].includes(props.config.align) ? props.config.align : 'center'));
// A visitor sees a phone only when it has a screen, and no block when
// neither phone does and there are no buttons; the editor keeps the
// empty frames to fill (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const screens = computed(() => smartphoneScreens(props.config));
const showFirst = computed(() => editing || screens.value.first);
const showSecond = computed(() => !!props.config.show_second && (editing || screens.value.second));
const secondPosition = computed(() => (props.config.second_position === 'left' ? 'left' : 'right'));
const buttons = computed(() => (editing ? (Array.isArray(props.config.buttons) ? props.config.buttons : []) : ctaButtons(props.config)));
const hasContent = computed(() => showFirst.value || showSecond.value || buttons.value.length > 0);
</script>

<template>
    <section v-if="hasContent" :id="config.anchor_id || null" class="th-block th-sp" :style="styleVars(config)">
        <div class="th-wrap th-sec">
            <div v-if="showFirst || showSecond" class="th-sp__group"
                 :class="[`th-sp__group--${align}`, { 'th-sp__group--second-left': showSecond && secondPosition === 'left', 'th-sp__group--has-second': showSecond }]">
                <SmartphonePreview v-if="showFirst"
                    :media-type="config.media_type || 'image'"
                    :image-url="config.image_url || ''"
                    :image-alt="config.image_alt || ''"
                    :video-url="config.video_url || ''"
                    :poster-url="config.poster_url || ''"
                    :bg-color="config.bg_color || 'linear-gradient(160deg,#0f0c29,#302b63,#24243e)'"
                    :frame-style="config.frame_style || 'dark'"
                    :max-width="Number(config.max_width) || 300"
                    :hide-topbar="!!config.hide_topbar"
                    :align="align"
                />
                <SmartphonePreview v-if="showSecond"
                                   :media-type="config.second_media_type || 'image'"
                                   :image-url="config.second_image_url || ''"
                                   :image-alt="config.second_image_alt || ''"
                                   :video-url="config.second_video_url || ''"
                                   :bg-color="config.second_bg_color || 'linear-gradient(160deg,#0f2027,#203a43,#2c5364)'"
                                   :frame-style="config.frame_style || 'dark'"
                                   :max-width="Math.round((Number(config.max_width) || 300) * 0.75)"
                                   :hide-topbar="!!config.hide_topbar"
                                   align="center"
                />
            </div>

            <div v-if="buttons.length" class="th-row th-sp__actions" :class="`th-sp__actions--${align}`">
                <a v-for="(b, i) in buttons" :key="i" :href="b.href || '#'"
                   class="th-btn"
                   :class="(b.style || 'primary') === 'ghost' ? 'th-btn--ghost' : 'th-btn--primary'">
                    {{ b.label }}
                </a>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-sp__group { display: flex; align-items: center; justify-content: center; gap: 24px; }
.th-sp__group--left { justify-content: flex-start; }
.th-sp__group--right { justify-content: flex-end; }
.th-sp__group--second-left { flex-direction: row-reverse; }
.th-sp__group--has-second { gap: clamp(20px, 4vw, 60px); }
.th-sp__actions { margin-top: 24px; justify-content: center; }
.th-sp__actions--left { justify-content: flex-start; }
.th-sp__actions--right { justify-content: flex-end; }

@media (max-width: 720px) {
    .th-sp__group--has-second { flex-direction: column; gap: 24px; }
}
</style>
